import { useState, useEffect } from "react";
import { bool, func } from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { DEFAULT_VERSION_INFO, zip } from "@repo/utils";
import { exportPdfVectorQuery, forceProjectSaveQuery, getShareLinkQuery } from "@query";
import { printPdf } from "@paths";
import {
  downloadFile,
  getProposalExportFileName,
  PDF_PRINT_PRESET,
  PDF_PRINT_VISIBILITY,
  PDF_USE_BORDERS,
  SHARE_SETTINGS,
  PDF_SHOW_PRICES,
} from "@utils";
import { useStores } from "@hooks";
import { PresetPicker, useEstimateEditorSettings, useEstimateEditorStore, useEstimatePresetStore } from "@tools";
import { Dialog, Button, TextField, InfoLabel, Alert, MenuButton } from "@components";
import { CircularProgress, Grid, MenuItem, Switch } from "@material-ui/core";
import { PdfFile } from "@assets";

export const PdfExportV2 = ({
  onClose,
  noSettingsDialog = false,
}) => {
  const { t } = useTranslation();
  const { stateStore } = useStores();
  const editorStore = useEstimateEditorStore();
  const presetStore = useEstimatePresetStore();
  const { isProposal, isSellerOrClient } = useEstimateEditorSettings();
  const { estimateUuid, ...proposalParams } = useParams();
  
  const [ showDialog, setShowDialog ] = useState(false);
  const [ isLoading, setLoading ] = useState(false);
  const [ alertText, setAlertText ] = useState("");
  const [ archiveAlertVisible, showArchiveAlert ] = useState(false);
  
  const [ orientation, setOrientation ] = useState(PAGE_ORIENTATION.PORTRAIT);
  const [ filename, setFilename ] = useState(getProposalExportFileName(editorStore.estimateName || estimateUuid, "pdf"));
  const [ presetId, setPresetId ] = useState(presetStore.selectedPresetId);
  const [ applyVisibility, setApplyVisibility ] = useState(true);
  const [ useBorders, setUseBorders ] = useState(false);
  
  useEffect(() => {
    if (showDialog)
      setFilename(getProposalExportFileName(editorStore.estimateName || estimateUuid, "pdf"));
  }, [editorStore.estimateName]);
  
  const exportPdf = async () => {
    setLoading(true);
    
    const options = {
      orientation,
      filename: filename,
      // printBackground,
    };
    let company = proposalParams.company, project = proposalParams.project, projectHash = proposalParams.projectHash;
    
    try {
      stateStore.resetFilesUploadProgress(1, true);
      
      if(!company && !project && !projectHash) {
        
        const shareLink = await getShareLinkQuery(estimateUuid);
        // Format: /proposal/{company}/{project}/{hash} or /proposal/{company}/{project}/{hash}/{version}
        const linkMatch = shareLink.match(/\/proposal\/([^/]+)\/([^/]+)\/([^/?]+)(?:\/([^/?]+))?/);
        
        if ( !linkMatch )
          throw new Error("Invalid share link format: ", shareLink);
        
        company = linkMatch[1];
        project = linkMatch[2];
        projectHash = linkMatch[3];
        // const [ , company, project, hash ] = linkMatch;
      }
      
      const versionKey = editorStore.currentVersionKey || DEFAULT_VERSION_INFO.key;
      
      const urlParams = { };
      let printUrl = `${import.meta.env.APP_APP_ORIGIN_DOMAIN}${printPdf(company, project, projectHash, versionKey)}`;
      
      if(presetId && presetId > 0)
        urlParams[PDF_PRINT_PRESET] = presetId;
      if(applyVisibility)
        urlParams[PDF_PRINT_VISIBILITY] = applyVisibility;
      if(useBorders)
        urlParams[PDF_USE_BORDERS] = useBorders;
      if(isSellerOrClient)
        urlParams[PDF_SHOW_PRICES] = true;
      if(editorStore.shareLinkSettings && Object.keys(editorStore.shareLinkSettings).length)
        urlParams[SHARE_SETTINGS] = zip(JSON.stringify(editorStore.shareLinkSettings));
      
      if(Object.keys(urlParams).length > 0)
        printUrl += "?" + Object.keys(urlParams).map(key => `${key}=${urlParams[key]}`).join("&");
      
      // console.log("Print url: ", printUrl);
      
      const baseFileName = options.filename || editorStore.estimateName || estimateUuid;
      const fileName = getProposalExportFileName(baseFileName, "pdf");
      
      if(editorStore.pdfDocument?.file?.path) {
        options.templatePdfUrl = editorStore.pdfDocument.file.path;
        options.templatePdfPages = editorStore.usedPdfPages;
        options.insertPagePlace = editorStore.proposalTablePosition;
      }
      
      if(editorStore.currentEstimateStructure.settings.isTimelineTableUsed)
        options.renderTimeline = true;
      
      const pdfBlob = await exportPdfVectorQuery(
        printUrl,
        {
          autoScale: true,
          printBackground: true,
          delayMs: DEFAULT_DELAY,
          format: DEFAULT_FORMAT,
          ...options,
        }
      );
      
      await downloadFile(
        fileName,
        pdfBlob
      );
      
      setShowDialog(false);
      onClose();
      
    } catch (e) {
      console.error("PDF Export V2 Error:", e);
      setAlertText(
        e.response?.data?.message ||
        e.message ||
        t("views.editor.export_error")
      );
    } finally {
      setLoading(false);
      stateStore.resetFilesUploadProgress();
    }
  };
  
  const fetchDialogData = async () => {
    await forceProjectSaveQuery(estimateUuid);
    if (!isProposal)
      await presetStore?.getPresets(t("views.editor.preset.apropo"));
    if(presetStore.presetChanged)
      await presetStore.updatePreset();
  }
  
  const closeAlert = () => setAlertText("");
  
  const handlePresetChange = (id) => setPresetId(id);
  
  const handleDialogOpen = async () => {
    if(editorStore.isArchived) {
      showArchiveAlert(true);
      return;
    }
    
    if(noSettingsDialog) {
      exportPdf();
      return;
    } else
      fetchDialogData();
    
    setShowDialog(true);
  }
  
  const handleClose = () => {
    if (!isLoading) {
      setShowDialog(false);
      onClose();
    }
  };

  return (
    <>
      <MenuButton
        className="justify-between"
        endIcon={
          isLoading
            ? <CircularProgress color="primary" size={18} />
            : <PdfFile className="color-primary" />
        }
        onClick={handleDialogOpen}
      >
        {t("views.editor.export_name.pdf")}
      </MenuButton>
      <Dialog
        open={showDialog}
        onClose={handleClose}
        title={t("views.editor.export_v2.title")}
        width={600}
        actions={
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              color="primary"
              disabled={isLoading}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={exportPdf}
              color="primary"
            >
              {
                isLoading
                  ? <CircularProgress size={20} />
                  : t("views.editor.export_v2.export_button")
              }
            </Button>
          </>
        }
      >
        <InfoLabel spacing={8} label="views.editor.export_v2.orientation.label" />
        <TextField
          select
          size="medium"
          value={orientation}
          onChange={(e) => setOrientation(e.target.value)}
        >
          {
            Object.values(PAGE_ORIENTATION).map((o) => (
              <MenuItem key={o} value={o}>
                {t(`views.editor.export_v2.orientation.value.${o}`)}
              </MenuItem>
            ))
          }
        </TextField>
        
        <InfoLabel spacing={8} label="views.editor.export_v2.filename" />
        <TextField
          value={filename}
          onChange={(e) => setFilename(e.target.value)}
          fullWidth
          size="medium"
          variant="outlined"
          helperText={t("views.editor.export_v2.filename_helper")}
        />
        {
          !isProposal &&
          <Grid item container justifyContent="space-between" className="mb-4">
            <InfoLabel label={t("views.editor.dialogs.share.settings.preset")} className="w-max m-0" />
            <PresetPicker
              presetId={presetId}
              onPresetChange={handlePresetChange}
              noPresetCreate
            />
          </Grid>
        }
        
        {
          !isProposal &&
          <Grid item container wrap="nowrap" className="mb-4">
            <InfoLabel spacing={0} label="views.editor.export_v2.apply_visibility" />
            <Switch
              size="medium"
              color="primary"
              checked={applyVisibility}
              onChange={() => setApplyVisibility(!applyVisibility)}
              name={
                applyVisibility
                  ? t("views.editor.export_v2.apply_visibility_ex")
                  : t("views.editor.export_v2.apply_visibility")
              }
            />
          </Grid>
        }
        
        <Grid item container wrap="nowrap" className="mb-4">
          <InfoLabel spacing={0} label="views.editor.export_v2.use_borders" />
          <Switch
            size="medium"
            color="primary"
            checked={useBorders}
            onChange={() => setUseBorders(!useBorders)}
            name={
              applyVisibility
              ? t("views.editor.export_v2.use_borders_ex")
              : t("views.editor.export_v2.use_borders")
          }
          />
        </Grid>
      </Dialog>
      {
        alertText &&
        <Alert
          isOpen={Boolean(alertText)}
          onAccept={closeAlert}
          title={t("common.error")}
          acceptText={t("common.close")}
        >
          {alertText}
        </Alert>
      }
      <Alert
        title={t("views.editor.pdf_archived_export_alert.title")}
        isOpen={archiveAlertVisible}
        onAccept={() => showArchiveAlert(false)}
        acceptText={t("common.close")}
      >
        {t("views.editor.pdf_archived_export_alert.content")}
      </Alert>
    </>
  );
};

PdfExportV2.propTypes = {
  // open: bool.isRequired,
  onClose: func.isRequired,
  noSettingsDialog: bool,
};


export const PAGE_FORMATS = [
  { value: "A4", label: "A4 (210 × 297 mm)" },
  { value: "A4", label: "A4 (297 × 210 mm)" },
  { value: "A3", label: "A3 (297 × 420 mm)" },
  { value: "A5", label: "A5 (148 × 210 mm)" },
  { value: "Letter", label: "Letter (8.5 × 11 in)" },
  { value: "Legal", label: "Legal (8.5 × 14 in)" },
  { value: "Tabloid", label: "Tabloid (11 × 17 in)" },
  { value: "custom", label: "Custom" },
];

export const PAGE_ORIENTATION = {
  PORTRAIT: "portrait",
  LANDSCAPE: "landscape",
};

export const DEFAULT_FORMAT = "A4";
export const DEFAULT_DELAY = 2000;